import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useRoute, useLocation, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { TagPicker } from "@/components/TagPicker";
import { 
  Building2, 
  Shield, 
  CheckCircle, 
  AlertCircle, 
  MapPin, 
  Truck, 
  Package,
  MessageSquare,
  FileCheck,
  ArrowLeft,
  Star,
  StarOff,
  Pencil,
  Settings,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import StarRating from "@/components/StarRating";
import { useState } from "react";
import { EQUIPMENT_TYPES } from "@/lib/equipmentTypes";

const FREIGHT_TYPE_OPTIONS = [
  "Dry Goods", "Electronics", "Industrial Equipment", "Perishables",
  "Hazardous Materials", "Automotive", "Building Materials", "Other"
];
const SHIPPING_LANE_OPTIONS = ["Southeast", "Northeast", "Midwest", "Southwest", "West", "National"];
const SPECIALIZATION_OPTIONS = [
  "LTL", "Temperature Controlled", "Heavy Haul", "Flatbed",
  "Dry Van", "Reefer", "Hazmat", "Other"
];
const MONTHLY_SHIPMENT_OPTIONS = [
  { label: "1–5", value: 1 },
  { label: "6–15", value: 6 },
  { label: "16–25", value: 16 },
  { label: "25+", value: 25 },
];

interface ProfileData {
  id: string;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  companyLogo: string | null;
  userType: string | null;
  dotNumber: string | null;
  mcNumber: string | null;
  hasInsurance: boolean | null;
  insuranceProvider: string | null;
  verificationStatus: string;
  adminVerified: boolean;
  upvoteCount: number;
  completedShipments: number;
  activeLoads: number;
  activeTrucks: number;
  fleetSize: number | null;
  truckTypes: string[] | null;
  operatingRegions: string[] | null;
  monthlyShipments: number | null;
  freightTypes: string[] | null;
  shippingLanes: string[] | null;
  yearsInBusiness: number | null;
  specializations: string[] | null;
  companyDescription: string | null;
  fmcsaData: any | null;
  isIntrastate: boolean | null;
  createdAt: string | null;
}

interface EditFormData {
  companyDescription: string;
  monthlyShipments: number | null;
  freightTypes: string[];
  shippingLanes: string[];
  yearsInBusiness: number | null;
  specializations: string[];
  truckTypes: string[];
  hasInsurance: boolean;
  insuranceProvider: string;
}

function buildEditData(profile: ProfileData): EditFormData {
  return {
    companyDescription: profile.companyDescription ?? "",
    monthlyShipments: profile.monthlyShipments ?? null,
    freightTypes: profile.freightTypes ?? [],
    shippingLanes: profile.shippingLanes ?? [],
    yearsInBusiness: profile.yearsInBusiness ?? null,
    specializations: profile.specializations ?? [],
    truckTypes: profile.truckTypes ?? [],
    hasInsurance: profile.hasInsurance ?? false,
    insuranceProvider: profile.insuranceProvider ?? "",
  };
}

export default function ProfilePage() {
  const [, params] = useRoute("/profile/:userId");
  const [, setLocation] = useLocation();
  const userId = params?.userId;
  const { user: currentUser } = useAuth();
  const { toast } = useToast();

  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState<EditFormData>({
    companyDescription: "",
    monthlyShipments: null,
    freightTypes: [],
    shippingLanes: [],
    yearsInBusiness: null,
    specializations: [],
    truckTypes: [],
    hasInsurance: false,
    insuranceProvider: "",
  });

  const isOwnProfileCheck = currentUser?.id === userId;

  // For secondary members viewing their own profile, show the account owner's company data
  const profileUserId = (isOwnProfileCheck && currentUser?.accountOwnerId)
    ? currentUser.accountOwnerId
    : userId;

  const { data: profile, isLoading, error } = useQuery<ProfileData>({
    queryKey: [`/api/users/${profileUserId}/profile`],
    enabled: !!profileUserId,
  });

  const { data: partnerStatus } = useQuery<{ isPartner: boolean }>({
    queryKey: [`/api/preferred-partners/${userId}/status`],
    enabled: !!userId && !isOwnProfileCheck,
  });

  const { data: userRating } = useQuery<{ averageRating: number | null; totalRatings: number }>({
    queryKey: [`/api/users/${userId}/rating`],
    enabled: !!userId,
  });

  const addPartnerMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/preferred-partners/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/preferred-partners/${userId}/status`] });
      queryClient.invalidateQueries({ queryKey: ["/api/preferred-partners"] });
      toast({ title: "Partner saved", description: "Added to your partners list." });
    },
    onError: () => toast({ title: "Error", description: "Failed to save partner.", variant: "destructive" }),
  });

  const removePartnerMutation = useMutation({
    mutationFn: async () => apiRequest("DELETE", `/api/preferred-partners/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/preferred-partners/${userId}/status`] });
      queryClient.invalidateQueries({ queryKey: ["/api/preferred-partners"] });
      toast({ title: "Partner removed", description: "Removed from your partners list." });
    },
    onError: () => toast({ title: "Error", description: "Failed to remove partner.", variant: "destructive" }),
  });

  const saveMutation = useMutation({
    mutationFn: async (data: EditFormData) => {
      return apiRequest("PATCH", "/api/profile", {
        companyDescription: data.companyDescription || null,
        monthlyShipments: data.monthlyShipments,
        freightTypes: data.freightTypes,
        shippingLanes: data.shippingLanes,
        yearsInBusiness: data.yearsInBusiness,
        specializations: data.specializations,
        truckTypes: data.truckTypes,
        hasInsurance: data.hasInsurance,
        insuranceProvider: data.insuranceProvider,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/users/${userId}/profile`] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setIsEditMode(false);
      toast({ title: "Profile updated", description: "Your changes have been saved." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save profile. Please try again.", variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center">
            <div className="animate-pulse space-y-4">
              <div className="h-24 w-24 bg-muted rounded-full mx-auto" />
              <div className="h-6 w-48 bg-muted rounded mx-auto" />
              <div className="h-4 w-32 bg-muted rounded mx-auto" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Profile Not Found</h2>
            <p className="text-muted-foreground mb-4">
              This member profile could not be found or you don't have permission to view it.
            </p>
            <Button onClick={() => setLocation("/dashboard")} data-testid="button-back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === profile.id;
  const memberSince = profile.createdAt 
    ? new Date(profile.createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "Unknown";

  const getInitials = () => {
    if (profile.company) return profile.company.substring(0, 2).toUpperCase();
    const first = profile.firstName?.charAt(0) || "";
    const last = profile.lastName?.charAt(0) || "";
    return (first + last).toUpperCase() || "??";
  };

  const getRoleIcon = () => {
    switch (profile.userType) {
      case "Carrier": return <Truck className="w-4 h-4" />;
      case "Shipper": return <Package className="w-4 h-4" />;
      case "Broker": return <Building2 className="w-4 h-4" />;
      default: return <Building2 className="w-4 h-4" />;
    }
  };

  const getRoleBadgeVariant = (): "default" | "secondary" | "outline" => {
    switch (profile.userType) {
      case "Carrier": return "default";
      case "Shipper": return "secondary";
      case "Broker": return "outline";
      default: return "secondary";
    }
  };

  const handleEditClick = () => {
    setEditData(buildEditData(profile));
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  const handleSave = () => {
    saveMutation.mutate(editData);
  };

  const descriptionLength = editData.companyDescription.length;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {!isOwnProfile && (
        <Button 
          variant="ghost" 
          onClick={() => window.history.back()}
          className="mb-4"
          data-testid="button-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      )}

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
            <Avatar className="w-24 h-24 border-2 border-border" data-testid="avatar-company">
              {profile.companyLogo && (
                <AvatarImage src={profile.companyLogo.startsWith('/objects/') ? profile.companyLogo : `/objects/${profile.companyLogo}`} alt={profile.company || "Company"} className="object-cover" />
              )}
              <AvatarFallback className="text-2xl bg-muted">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <CardTitle className="text-2xl" data-testid="text-company-name">
                  {profile.company || `${profile.firstName} ${profile.lastName}`}
                </CardTitle>
                {profile.adminVerified && (
                  <Badge variant="default" className="bg-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-center text-muted-foreground">
                <Badge variant={getRoleBadgeVariant()}>
                  {getRoleIcon()}
                  <span className="ml-1">{profile.userType}</span>
                </Badge>
                {profile.isIntrastate && (
                  <Badge variant="outline" data-testid="badge-intrastate">
                    <MapPin className="w-3 h-3 mr-1" />
                    Intrastate Carrier
                  </Badge>
                )}
                <span className="text-sm">Member since {memberSince}</span>
              </div>
            </div>

            {isOwnProfile && !isEditMode && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="default"
                  onClick={handleEditClick}
                  data-testid="button-edit-profile"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
                <Link href="/settings">
                  <Button variant="ghost" size="default" data-testid="link-account-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Button>
                </Link>
              </div>
            )}

            {isOwnProfile && isEditMode && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  data-testid="button-save-profile"
                >
                  {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saveMutation.isPending}
                  data-testid="button-cancel-edit"
                >
                  Cancel
                </Button>
              </div>
            )}

            {!isOwnProfile && (
              <div className="flex items-center gap-2 flex-wrap">
                {partnerStatus?.isPartner ? (
                  <Button
                    variant="outline"
                    onClick={() => removePartnerMutation.mutate()}
                    disabled={removePartnerMutation.isPending || addPartnerMutation.isPending}
                    data-testid="button-remove-partner"
                  >
                    <StarOff className="w-4 h-4 mr-2" />
                    {removePartnerMutation.isPending ? "Removing..." : "Remove Partner"}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => addPartnerMutation.mutate()}
                    disabled={addPartnerMutation.isPending || removePartnerMutation.isPending}
                    data-testid="button-save-partner"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    {addPartnerMutation.isPending ? "Saving..." : "Save as Partner"}
                  </Button>
                )}
                <Button 
                  onClick={() => setLocation(`/shipments?user=${profile.id}`)}
                  data-testid="button-contact"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Contact
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Stats tiles — no section header */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <CheckCircle className="w-5 h-5 mx-auto mb-2 text-green-600" />
              <p className="text-2xl font-bold" data-testid="text-completed-shipments">{profile.completedShipments}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg" data-testid="stat-rating">
              <div className="flex justify-center mb-2">
                <StarRating value={userRating?.averageRating ?? null} readOnly fractional size="sm" />
              </div>
              <p className="text-2xl font-bold" data-testid="text-rating">
                {userRating?.averageRating != null ? userRating.averageRating.toFixed(1) : '—'}
              </p>
              <p className="text-sm text-muted-foreground">
                Rating{userRating?.totalRatings ? ` (${userRating.totalRatings})` : ''}
              </p>
            </div>
            {(profile.userType === "Shipper" || profile.userType === "Broker") && (
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Package className="w-5 h-5 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold" data-testid="text-active-loads">{profile.activeLoads}</p>
                <p className="text-sm text-muted-foreground">Active Loads</p>
              </div>
            )}
            {(profile.userType === "Carrier" || profile.userType === "Broker") && (
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Truck className="w-5 h-5 mx-auto mb-2 text-orange-600" />
                <p className="text-2xl font-bold" data-testid="text-active-trucks">{profile.activeTrucks}</p>
                <p className="text-sm text-muted-foreground">Active Trucks</p>
              </div>
            )}
          </div>

          <Separator />

          {/* Company Description */}
          {isEditMode ? (
            <div className="space-y-2">
              <Label htmlFor="companyDescription">Company Description</Label>
              <Textarea
                id="companyDescription"
                rows={5}
                placeholder="Tell others about your company..."
                value={editData.companyDescription}
                onChange={e => {
                  if (e.target.value.length <= 500) {
                    setEditData(prev => ({ ...prev, companyDescription: e.target.value }));
                  }
                }}
                data-testid="textarea-company-description"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Keep it professional. This is visible to all members on Fill The Trucks.</p>
                <span className={`text-xs ${descriptionLength > 480 ? "text-orange-500" : "text-muted-foreground"}`}>
                  {descriptionLength}/500
                </span>
              </div>
            </div>
          ) : profile.companyDescription ? (
            <div>
              <h3 className="font-semibold mb-2">About</h3>
              <p className="text-muted-foreground leading-relaxed" data-testid="text-company-description">
                {profile.companyDescription}
              </p>
            </div>
          ) : null}

          {(isEditMode || profile.companyDescription) && <Separator />}

          {/* Credentials - For Carriers and Brokers */}
          {(profile.userType === "Carrier" || profile.userType === "Broker") && (
            <>
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Credentials & Compliance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* DOT Number — read-only even in edit mode */}
                  <div className={`p-4 border rounded-lg ${isEditMode ? "bg-muted/40 cursor-not-allowed select-none" : ""}`}>
                    <p className="text-sm text-muted-foreground">DOT Number</p>
                    {profile.dotNumber ? (
                      <>
                        <p className="text-lg font-semibold text-muted-foreground" data-testid="text-dot-number">
                          {profile.dotNumber}
                        </p>
                        {!isEditMode && profile.fmcsaData?.allowedToOperate && (
                          <Badge variant="outline" className="mt-2 text-green-600 border-green-600">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active Authority
                          </Badge>
                        )}
                      </>
                    ) : (
                      <p className="text-muted-foreground" data-testid="text-dot-number">Not provided</p>
                    )}
                    {isEditMode && (
                      <p className="text-xs text-muted-foreground mt-1">
                        To update your DOT number, email hello@fillthetrucks.com
                      </p>
                    )}
                  </div>

                  {/* MC Number — read-only even in edit mode */}
                  <div className={`p-4 border rounded-lg ${isEditMode ? "bg-muted/40 cursor-not-allowed select-none" : ""}`}>
                    <p className="text-sm text-muted-foreground">MC Number</p>
                    {profile.mcNumber ? (
                      <p className="text-lg font-semibold text-muted-foreground" data-testid="text-mc-number">
                        {profile.mcNumber}
                      </p>
                    ) : profile.isIntrastate ? (
                      <p className="text-sm text-muted-foreground" data-testid="text-mc-number">Not required (intrastate carrier)</p>
                    ) : (
                      <p className="text-muted-foreground" data-testid="text-mc-number">Not provided</p>
                    )}
                    {isEditMode && (
                      <p className="text-xs text-muted-foreground mt-1">
                        To update your MC number, email hello@fillthetrucks.com
                      </p>
                    )}
                  </div>

                  {profile.isIntrastate && !isEditMode && (
                    <div className="p-4 border rounded-lg md:col-span-2 bg-muted/40" data-testid="notice-intrastate">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                        <p className="text-sm font-medium">Intrastate Carrier</p>
                      </div>
                      <p className="text-sm text-muted-foreground">This carrier operates within their state only and cannot cross state lines.</p>
                    </div>
                  )}

                  {/* Insurance */}
                  {isEditMode && isOwnProfile ? (
                    <div className="p-4 border rounded-lg md:col-span-2 space-y-3">
                      <p className="text-sm text-muted-foreground font-medium">Insurance</p>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="hasInsurance"
                          checked={editData.hasInsurance}
                          onCheckedChange={checked => setEditData(prev => ({ ...prev, hasInsurance: checked === true }))}
                          data-testid="checkbox-has-insurance"
                        />
                        <Label htmlFor="hasInsurance" className="cursor-pointer">
                          I have active cargo/liability insurance
                        </Label>
                      </div>
                      {editData.hasInsurance && (
                        <div className="space-y-1 pl-6">
                          <Label htmlFor="insuranceProvider">Insurance Provider (Optional)</Label>
                          <Input
                            id="insuranceProvider"
                            value={editData.insuranceProvider}
                            onChange={e => setEditData(prev => ({ ...prev, insuranceProvider: e.target.value }))}
                            placeholder="e.g., Progressive, Great West Casualty"
                            data-testid="input-insurance-provider"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">Insurance</p>
                      {profile.hasInsurance ? (
                        <>
                          <div className="flex items-center gap-2 mt-1">
                            <FileCheck className="w-5 h-5 text-green-600" />
                            <span className="font-semibold text-green-600" data-testid="text-insurance-status">Insured</span>
                          </div>
                          {profile.insuranceProvider && (
                            <p className="text-sm text-muted-foreground mt-1">{profile.insuranceProvider}</p>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <AlertCircle className="w-5 h-5 text-muted-foreground" />
                          <span className="text-muted-foreground">Not specified</span>
                        </div>
                      )}
                    </div>
                  )}

                  {!isEditMode && profile.fmcsaData?.legalName && (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">Legal Name (FMCSA)</p>
                      <p className="font-semibold" data-testid="text-legal-name">{profile.fmcsaData.legalName}</p>
                    </div>
                  )}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Carrier-specific info */}
          {profile.userType === "Carrier" && (
            <>
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Fleet Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {profile.fleetSize != null && (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">Fleet Size</p>
                      <p className="text-lg font-semibold">{profile.fleetSize} trucks</p>
                    </div>
                  )}

                  {/* Equipment Types (truckTypes) */}
                  {isEditMode && isOwnProfile ? (
                    <div className="space-y-1">
                      <Label>Equipment Types</Label>
                      <TagPicker
                        options={EQUIPMENT_TYPES}
                        value={editData.truckTypes}
                        onChange={v => setEditData(prev => ({ ...prev, truckTypes: v }))}
                        placeholder="Select equipment types..."
                        data-testid="tagpicker-equipment-types"
                      />
                    </div>
                  ) : profile.truckTypes && profile.truckTypes.length > 0 ? (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">Equipment Types</p>
                      <div className="flex flex-wrap gap-1">
                        {profile.truckTypes.map((type, idx) => (
                          <Badge key={idx} variant="secondary">{type}</Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Years in Business */}
                  {isEditMode && isOwnProfile ? (
                    <div className="space-y-1">
                      <Label htmlFor="yearsInBusiness">Years in Business</Label>
                      <Input
                        id="yearsInBusiness"
                        type="number"
                        min={0}
                        value={editData.yearsInBusiness ?? ""}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          yearsInBusiness: e.target.value === "" ? null : Number(e.target.value)
                        }))}
                        placeholder="e.g., 5"
                        data-testid="input-years-in-business"
                      />
                    </div>
                  ) : profile.yearsInBusiness != null ? (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">Years in Business</p>
                      <p className="text-lg font-semibold">{profile.yearsInBusiness} years</p>
                    </div>
                  ) : null}

                  {/* Specializations */}
                  {isEditMode && isOwnProfile ? (
                    <div className="space-y-1 md:col-span-2">
                      <Label>Specializations</Label>
                      <TagPicker
                        options={SPECIALIZATION_OPTIONS}
                        value={editData.specializations}
                        onChange={v => setEditData(prev => ({ ...prev, specializations: v }))}
                        placeholder="Select specializations..."
                        data-testid="tagpicker-specializations"
                      />
                    </div>
                  ) : profile.specializations && profile.specializations.length > 0 ? (
                    <div className="p-4 border rounded-lg md:col-span-2">
                      <p className="text-sm text-muted-foreground mb-2">Specializations</p>
                      <div className="flex flex-wrap gap-1">
                        {profile.specializations.map((spec, idx) => (
                          <Badge key={idx} variant="secondary">{spec}</Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {!isEditMode && profile.operatingRegions && profile.operatingRegions.length > 0 && (
                    <div className="p-4 border rounded-lg md:col-span-2">
                      <p className="text-sm text-muted-foreground mb-2">Operating Regions</p>
                      <div className="flex flex-wrap gap-1">
                        {profile.operatingRegions.map((region, idx) => (
                          <Badge key={idx} variant="outline">
                            <MapPin className="w-3 h-3 mr-1" />
                            {region}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Shipper-specific info */}
          {profile.userType === "Shipper" && (
            <>
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Shipping Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Monthly Shipments */}
                  {isEditMode && isOwnProfile ? (
                    <div className="space-y-1">
                      <Label htmlFor="monthlyShipments">Monthly Shipments</Label>
                      <select
                        id="monthlyShipments"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={editData.monthlyShipments ?? ""}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          monthlyShipments: e.target.value === "" ? null : Number(e.target.value)
                        }))}
                        data-testid="select-monthly-shipments"
                      >
                        <option value="">Select range...</option>
                        {MONTHLY_SHIPMENT_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  ) : profile.monthlyShipments != null ? (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">Monthly Shipments</p>
                      <p className="text-lg font-semibold">
                        {MONTHLY_SHIPMENT_OPTIONS.find(o => o.value === profile.monthlyShipments)?.label ?? `${profile.monthlyShipments}+`}
                      </p>
                    </div>
                  ) : null}

                  {/* Freight Types */}
                  {isEditMode && isOwnProfile ? (
                    <div className="space-y-1">
                      <Label>Freight Types</Label>
                      <TagPicker
                        options={FREIGHT_TYPE_OPTIONS}
                        value={editData.freightTypes}
                        onChange={v => setEditData(prev => ({ ...prev, freightTypes: v }))}
                        placeholder="Select freight types..."
                        data-testid="tagpicker-freight-types"
                      />
                    </div>
                  ) : profile.freightTypes && profile.freightTypes.length > 0 ? (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">Freight Types</p>
                      <div className="flex flex-wrap gap-1">
                        {profile.freightTypes.map((type, idx) => (
                          <Badge key={idx} variant="secondary">{type}</Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Shipping Lanes */}
                  {isEditMode && isOwnProfile ? (
                    <div className="space-y-1 md:col-span-2">
                      <Label>Shipping Lanes</Label>
                      <TagPicker
                        options={SHIPPING_LANE_OPTIONS}
                        value={editData.shippingLanes}
                        onChange={v => setEditData(prev => ({ ...prev, shippingLanes: v }))}
                        placeholder="Select shipping lanes..."
                        data-testid="tagpicker-shipping-lanes"
                      />
                    </div>
                  ) : profile.shippingLanes && profile.shippingLanes.length > 0 ? (
                    <div className="p-4 border rounded-lg md:col-span-2">
                      <p className="text-sm text-muted-foreground mb-2">Shipping Lanes</p>
                      <div className="flex flex-wrap gap-1">
                        {profile.shippingLanes.map((lane, idx) => (
                          <Badge key={idx} variant="outline">
                            <MapPin className="w-3 h-3 mr-1" />
                            {lane}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Broker-specific info */}
          {profile.userType === "Broker" && (
            <>
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Brokerage Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Years in Business */}
                  {isEditMode && isOwnProfile ? (
                    <div className="space-y-1">
                      <Label htmlFor="brokerYearsInBusiness">Years in Business</Label>
                      <Input
                        id="brokerYearsInBusiness"
                        type="number"
                        min={0}
                        value={editData.yearsInBusiness ?? ""}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          yearsInBusiness: e.target.value === "" ? null : Number(e.target.value)
                        }))}
                        placeholder="e.g., 5"
                        data-testid="input-years-in-business"
                      />
                    </div>
                  ) : profile.yearsInBusiness != null ? (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">Years in Business</p>
                      <p className="text-lg font-semibold">{profile.yearsInBusiness} years</p>
                    </div>
                  ) : null}

                  {/* Equipment Types (truckTypes) */}
                  {isEditMode && isOwnProfile ? (
                    <div className="space-y-1">
                      <Label>Equipment Types</Label>
                      <TagPicker
                        options={EQUIPMENT_TYPES}
                        value={editData.truckTypes}
                        onChange={v => setEditData(prev => ({ ...prev, truckTypes: v }))}
                        placeholder="Select equipment types..."
                        data-testid="tagpicker-equipment-types"
                      />
                    </div>
                  ) : profile.truckTypes && profile.truckTypes.length > 0 ? (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">Equipment Types</p>
                      <div className="flex flex-wrap gap-1">
                        {profile.truckTypes.map((type, idx) => (
                          <Badge key={idx} variant="secondary">{type}</Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Specializations */}
                  {isEditMode && isOwnProfile ? (
                    <div className="space-y-1 md:col-span-2">
                      <Label>Specializations</Label>
                      <TagPicker
                        options={SPECIALIZATION_OPTIONS}
                        value={editData.specializations}
                        onChange={v => setEditData(prev => ({ ...prev, specializations: v }))}
                        placeholder="Select specializations..."
                        data-testid="tagpicker-specializations"
                      />
                    </div>
                  ) : profile.specializations && profile.specializations.length > 0 ? (
                    <div className="p-4 border rounded-lg md:col-span-2">
                      <p className="text-sm text-muted-foreground mb-2">Specializations</p>
                      <div className="flex flex-wrap gap-1">
                        {profile.specializations.map((spec, idx) => (
                          <Badge key={idx} variant="secondary">{spec}</Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
